<template>
  <BaseTooltip :direction="direction" no-padding>
    <slot></slot>
    <template #content>
      <div
        class="d-flex main-container"
        :class="{ 
          'dubbing-tooltip': itemType === 'dubbings' && showTags,
          'has-error': hasAnyError
        }"
      >
        <div
          class="d-flex flex-column item-container card-language-list"
          v-for="key in itemsKeys"
          :key="key"
        >
          <div class="d-flex align-center border-bottom-primary">
            <div
              style="max-width: 30px; max-height: 24px"
              v-if="items[key].iconFile"
            ></div>
            <span class="body-bold-smaller neutral-30-base--text mb-2 px-5 pt-4">
              {{ items[key].label }}
            </span>
          </div>
          <div
            v-for="item in items[key].items.slice().sort((a, b) => {
              if (a.isOriginal) return -1
              if (b.isOriginal) return 1
              return a.srclang.localeCompare(b.srclang)
            })"
            :key="`${key}__${item.srclang}__${item.id}`"
            class="d-flex align-center pt-3 pb-2 px-5 tooltipItem"
          >
            <template v-if="getCountryCodeBySrcLang(item.srclang)">
              <FlagCards
                class="mb-1"
                :items="[item.hasError ? 'error' : getCountryCodeBySrcLang(item.srclang)]"
              />
              <span class="body-regular-smaller neutral-30-base--text ml-2 mb-1">
                {{ getLangCodeBySrcLang(item.srclang).label }}
                <template v-if="item.srclang === originalLang"
                  >(Original)</template
                >
              </span>
              <span
                v-if="item.srclang !== originalLang"
                class="ml-auto pl-2 mb-1 d-flex align-center"
              >
                <template v-if="itemType === 'subtitles' || !showTags">
                  <simple-svg
                    v-if="item.isAI"
                    :src="require('@/assets/IconAI.svg')"
                    style="height: 16px"
                  />
                </template>
                <template v-else-if="itemType === 'dubbings'">
                  <v-tooltip
                    bottom
                    v-if="getDubbingTag(item)"
                    :disabled="!getDubbingTag(item).tooltip"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-tag
                        type="neutral"
                        size="xsmall"
                        class="ml-2"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <span class="body-bold-smaller neutral-30-base--text px-1">
                          {{ getDubbingTag(item).text }}
                        </span>
                      </v-tag>
                    </template>
                    <span style="white-space: pre-line;">
                      {{ getDubbingTag(item).tooltip }}
                    </span>
                  </v-tooltip>
                </template>
                <template v-if="item.hasError">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <base-btn
                        type="neutral"
                        class="panda-icons --error ma-0 ml-1"
                        v-bind="attrs"
                        v-on="on"
                        @click="$emit('error-click', item)"
                      >
                        <img
                          style="display: flex"
                          :src="
                            $vuetify.theme.dark
                              ? require('@/assets/remove-dark.svg')
                              : require('@/assets/remove.svg')
                          "
                          fill-class-name="fill-color"
                          fill="var(--v-brand-lighten1)"
                          width="16px"
                        />
                      </base-btn>
                    </template>
                    <span>{{ $t('message.pages.video.delete') }}</span>
                  </v-tooltip>
                </template>
              </span>
            </template>
          </div>
        </div>
        <v-divider
          v-if="itemsKeys.length > 1"
          color="gray"
          class="vertical-divider-position"
          vertical
        ></v-divider>
      </div>
    </template>
    <template #actions>
      <slot name="actions"></slot>
    </template>
  </BaseTooltip>
</template>

<script>
import BaseTooltip from './BaseTooltip.vue'
import langCodes from '@/utils/langCodes.mixin.js'
import FlagCards from '@/components/FlagCards.vue'

export default {
  name: 'FlagTooltip',
  components: { BaseTooltip, FlagCards },
  mixins: [langCodes],
  props: {
    items: {
      type: Object,
      required: true,
    },
    originalLang: {
      type: String,
      default: '',
    },
    direction: {
      type: String,
      default: 'bottom',
    },
    itemType: {
      type: String,
      required: true,
      validator: value => ['subtitles', 'dubbings', 'originalLang'].includes(value),
    },
    showTags: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    /* Example of "items" prop
    items: {
      subtitles: {
        label: 'Legenda',
        iconFile: 'cc_tooltip',
        items: ['pt', 'en', 'es'],
      },
      audios: {
        label: 'Dublagem',
        iconFile: 'dubbing',
        items: ['pt'],
      },
    },*/
  }),
  computed: {
    itemsKeys() {
      return Object.keys(this.items)
    },
    hasAnyError() {
      return this.itemsKeys.some(key => 
        this.items[key].items.some(item => item.hasError)
      )
    }
  },
  methods: {
    getDubbingTag(item) {
      if (item.isOriginal) {
        return {
          text: this.$t('message.ai.dubbings.original'),
          tooltip: this.$t('message.ai.dubbings.originalTooltip'),
        }
      }
      if (item.isUploaded) {
        return {
          text: this.$t('message.ai.dubbings.sent'),
          tooltip: this.$t('message.ai.dubbings.sentTooltip'),
        }
      }
      if (item.isLipsync) {
        return {
          text: this.$t('message.ai.dubbings.dubbingLipSync'),
          tooltip: this.$t('message.ai.dubbings.dubbingLipSyncTooltip'),
        }
      }
      return {
        text: this.$t('message.ai.dubbings.dubbingAI'),
        tooltip: this.$t('message.ai.dubbings.dubbingAITooltip'),
      }
    },
  },
  emits: ['error-click'],
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_ai_works';

.main-container {
  gap: 24px;
  position: relative;
}
.vertical-divider-position {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-400%, 0);
}

.tooltipItem {
  border-top: 1px solid varFigma(neutral-95-lightest);
  min-width: 200px;
  max-width: 220px;
}

.dubbing-tooltip {
  width: 264px !important;
  .tooltipItem {
    width: 264px !important;
    max-width: 264px !important;
  }
}

.dubbing-tooltip.has-error {
  width: 284px !important;
  .tooltipItem {
    width: 284px !important;
    max-width: 284px !important;
  }
}

::v-deep .base-button.panda-icons.--error {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  padding: 0 !important;
  min-width: 24px !important;
  width: 24px !important;
  height: 24px !important;

  img {
    width: 16px !important;
    height: 16px !important;
  }
}
</style>
